(function () {
  'use strict';

  // Require EVERY variant option on the product to be selected before the
  // product form can submit. Works on PDPs and on dynamically-injected
  // quick-view modals by scanning for form[data-product-form] on load and
  // via a MutationObserver, so any new product form gets wired up as soon
  // as it appears in the DOM.

  const wiredForms = new WeakSet();
  const wiredButtons = new WeakSet();

  function optionLabelFromSelect(selectEl) {
    // name="options[Size Type]" -> "Size Type"
    const match = selectEl.name.match(/^options\[(.+)\]$/);
    return match ? match[1] : '';
  }

  function wireForm(productForm) {
    if (!productForm || wiredForms.has(productForm)) return;

    // Scope all variant-picker lookups to the same product section container,
    // so PDP and quick-view modals don't cross-wire on the same page.
    const container =
      productForm.closest('[data-section-type="product"]') ||
      productForm.closest('[data-is-quick-view]') ||
      productForm.parentElement;
    if (!container) return;

    const optionSelects = container.querySelectorAll('select[name^="options["]');
    if (!optionSelects.length) return;

    const options = [];
    optionSelects.forEach(select => {
      const wrapper = select.closest('.product__option');
      if (!wrapper) return;
      options.push({
        select: select,
        wrapper: wrapper,
        chips: wrapper.querySelectorAll('.product__chip'),
        label: optionLabelFromSelect(select)
      });
    });

    const addToCartBtn = productForm.querySelector('[data-add-to-cart]');
    if (!addToCartBtn || !options.length) return;

    wiredForms.add(productForm);

    const addToCartTextEl = addToCartBtn.querySelector('[data-add-to-cart-text]');
    const langAvailable = addToCartBtn.dataset.langAvailable;

    function hasUnselectedOption() {
      return options.some(opt => !opt.select.value);
    }

    // The theme's variantAvailability crosses out option chips whose value
    // can't be matched against currently-selected earlier options. When an
    // earlier option is still unselected (empty string), no variant's
    // option1/option2 will match "", so the theme incorrectly crosses out
    // every dependent chip. Un-cross-out those groups in that case.
    function clearBogusCrossouts() {
      for (let i = 1; i < options.length; i++) {
        const anyPriorUnselected = options.slice(0, i).some(opt => !opt.select.value);
        if (anyPriorUnselected) {
          options[i].chips.forEach(chip => chip.classList.remove('disabled'));
        }
      }
    }

    // Force every option to start unselected so the validator has something
    // to catch on every axis (Size Type, Size, etc.).
    setTimeout(() => {
      options.forEach(opt => {
        opt.select.selectedIndex = -1;
        opt.chips.forEach(chip => chip.classList.remove('selected'));
      });
      clearBogusCrossouts();
    }, 50);

    // With any option unselected, the theme can't resolve a matching variant
    // and flips the ATC button to "Unavailable" (disabled). Override that so
    // the user can click Add to Bag and get the per-option error. Only
    // override while an option is unselected — real sold-out / unavailable
    // states on a fully-selected variant are left alone.
    function restoreAddToBagIfOptionMissing() {
      if (!hasUnselectedOption()) return false;
      let changed = false;
      if (addToCartBtn.hasAttribute('disabled')) {
        addToCartBtn.removeAttribute('disabled');
        changed = true;
      }
      if (addToCartBtn.hasAttribute('aria-disabled')) {
        addToCartBtn.removeAttribute('aria-disabled');
        changed = true;
      }
      if (
        addToCartTextEl &&
        langAvailable &&
        addToCartTextEl.textContent.trim() !== langAvailable
      ) {
        addToCartTextEl.textContent = langAvailable;
        changed = true;
      }
      return changed;
    }

    if (!wiredButtons.has(addToCartBtn)) {
      wiredButtons.add(addToCartBtn);
      const btnObserver = new MutationObserver(() => {
        btnObserver.disconnect();
        restoreAddToBagIfOptionMissing();
        btnObserver.observe(addToCartBtn, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
          characterData: true
        });
      });
      btnObserver.observe(addToCartBtn, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    function showInlineErrorFor(opt) {
      if (opt.wrapper.querySelector('.size-error-message')) return;
      const error = document.createElement('div');
      error.className = 'size-error-message';
      error.textContent = 'Select a ' + opt.label;
      opt.wrapper.appendChild(error);
    }

    function clearInlineErrorFor(opt) {
      const error = opt.wrapper.querySelector('.size-error-message');
      if (error) error.remove();
    }

    function shakeButton() {
      addToCartBtn.classList.add('btn-shake');
      setTimeout(() => {
        addToCartBtn.classList.remove('btn-shake');
      }, 600);
    }

    function scrollToFirstMissing() {
      const first = options.find(opt => !opt.select.value);
      if (first) {
        first.wrapper.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }

    // Capture phase + stopImmediatePropagation ensures we run before the
    // theme's own submit handler, so it can't proceed past preventDefault.
    productForm.addEventListener('submit', (e) => {
      const missing = options.filter(opt => !opt.select.value);
      if (missing.length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        missing.forEach(showInlineErrorFor);
        shakeButton();
        scrollToFirstMissing();
      }
    }, true);

    options.forEach(opt => {
      opt.select.addEventListener('change', () => {
        clearInlineErrorFor(opt);
        // Defer so the theme's own change handler runs first and we can
        // un-do any cross-outs it added based on empty prior options.
        requestAnimationFrame(clearBogusCrossouts);
      });
      opt.chips.forEach(chip => {
        chip.addEventListener('click', () => {
          clearInlineErrorFor(opt);
          requestAnimationFrame(clearBogusCrossouts);
        });
      });
    });
  }

  function scan(root) {
    const scope = root || document;
    if (scope.querySelectorAll) {
      scope.querySelectorAll('form[data-product-form]').forEach(wireForm);
    }
  }

  function start() {
    scan(document);

    // Watch for dynamically added product forms (quick-view modal, etc.)
    const bodyObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('form[data-product-form]')) {
            wireForm(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('form[data-product-form]').forEach(wireForm);
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();